import "../lib/responsiveLayoutMetrics";
// src/components/Header.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  FaBars, FaTimes, FaUsers, FaCrown, FaUserCircle,
  FaCompass, FaChevronDown, FaPlusCircle, FaMapMarkedAlt, FaHotel,
  FaBolt, FaShoppingCart, FaPassport, FaInfoCircle, FaFire, FaStore, FaHeadset,
  FaTicketAlt, FaLocationArrow, FaMapMarkerAlt, FaHome, FaSearch, FaPlane, FaShieldAlt, FaBookOpen, FaSignOutAlt, FaCalendarCheck, FaShoppingBag, FaHeart, FaBell, FaArrowRight, FaGift, FaMagic, FaCommentDots, FaRobot
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';

// Import de notre nouvelle barre de recherche globale
import SearchOmnibar from './SearchOmnibar';

// Sélecteur de langue global Forfeo
import LanguageSwitcher from './language/LanguageSwitcher';
import { supabase } from '../supabaseClient';
import {
  getForfeoPortalForPath,
  readRememberedForfeoPortal,
  rememberForfeoPortalPath,
  resolveForfeoPortals,
  type ForfeoPortalAccess,
} from '../lib/forfeoPortals';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const [isBonsPlansOpen, setIsBonsPlansOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [availablePortals, setAvailablePortals] = useState<ForfeoPortalAccess[]>([]);
  const [lastPortal, setLastPortal] = useState<ForfeoPortalAccess | null>(null);

  // Nouvel état pour la barre de recherche
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // États actifs
  const isHome = location.pathname === '/';
  const isAbout = location.pathname === '/about';
  const isVisitQuebec = location.pathname === '/visiter-quebec';
  const isDiscoverQuebec = location.pathname === '/decouvrir-quebec' || location.pathname === '/discover-quebec';
  const isForfeoFinds = location.pathname === '/forfeo-finds';
  const isBoutique = location.pathname.startsWith('/boutique');
  const isServiceClient = location.pathname === '/service-client';
  const isHowItWorks = location.pathname === '/comment-ca-marche';
  const isTrust = location.pathname === '/confiance';
  const isHelp = location.pathname === '/aide';
  const isPartners = location.pathname === '/partenaires';
  const isGiftCard = location.pathname === '/carte-cadeau';
  const isGiftMoment = location.pathname === '/offrir-un-moment' || location.pathname === '/gift-a-moment';
  const isOccasionCalendar = location.pathname === '/calendrier-des-occasions' || location.pathname === '/occasion-calendar';
  const isSurpriseReveal = location.pathname === '/revelation-surprise' || location.pathname === '/surprise-reveal' || location.pathname === '/reveler-mon-cadeau';
  const isExperienceReview = location.pathname === '/avis-experience' || location.pathname === '/experience-review' || location.pathname === '/avis-apres-experience';
  const isInfoActive = isAbout || isServiceClient || isHowItWorks || isTrust || isHelp || isPartners || isGiftCard || isGiftMoment || isOccasionCalendar || isSurpriseReveal || isExperienceReview;
  const isPrevente = location.pathname === '/prevente';
  const isPassport = location.pathname === '/create-passport';
  const isDrops = location.pathname === '/drops';
  const isClub = location.pathname === '/club';
  const isEvasion = location.pathname === '/evasion' || location.pathname === '/alertes-intelligentes' || location.pathname === '/smart-travel-alerts' || location.pathname === '/mes-alertes-intelligentes';
  const isLastCall = location.pathname === '/last-call';
  const isExplorerPlus = location.pathname === '/explorer-plus';
  const isHabitat = location.pathname.startsWith('/habitat');
  const isAccountActive =
    location.pathname === '/gestion-compte' ||
    location.pathname === '/mon-compte' ||
    location.pathname === '/dashboard-voyageur' ||
    location.pathname === '/mes-reservations' ||
    location.pathname === '/mes-achats' ||
    location.pathname === '/mes-favoris' ||
    location.pathname === '/mes-alertes' ||
    Boolean(getForfeoPortalForPath(location.pathname));

  // @ts-ignore
  const { setIsCartOpen, cartCount } = useCart();

  const accountLabel = useMemo(() => {
    const email = currentUser?.email || '';
    const fullName =
      currentUser?.user_metadata?.full_name ||
      currentUser?.user_metadata?.name ||
      currentUser?.user_metadata?.first_name;

    if (fullName) return String(fullName).split(' ')[0];
    if (email) return email.split('@')[0];
    return t('navigation.myAccount', { defaultValue: 'Mon compte' });
  }, [currentUser, t]);

  const isLoggedIn = !!currentUser;

  const portalIcon = (portal: ForfeoPortalAccess) => {
    if (portal.iconKey === 'business') return <FaStore className="text-emerald-600" />;
    if (portal.iconKey === 'habitat') return <FaHome className="text-teal-600" />;
    if (portal.iconKey === 'pro') return <FaShieldAlt className="text-amber-600" />;
    if (portal.iconKey === 'affiliate') return <FaCrown className="text-purple-600" />;
    if (portal.iconKey === 'prp') return <FaUsers className="text-indigo-600" />;
    if (portal.iconKey === 'admin') return <FaShieldAlt className="text-red-600" />;
    return <FaPassport className="text-blue-600" />;
  };

  const portalLinks = useMemo(() => availablePortals.map((portal) => ({
    ...portal,
    icon: portalIcon(portal),
    active: portal.activePrefixes.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)),
  })), [availablePortals, location.pathname]);

  const smartReturnPortal = useMemo(() => {
    if (!lastPortal) return null;
    const allowed = availablePortals.some((portal) => portal.id === lastPortal.id) || lastPortal.id === 'traveler';
    if (!allowed) return null;
    const alreadyThere = lastPortal.activePrefixes.some((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));
    if (alreadyThere) return null;
    return lastPortal;
  }, [availablePortals, lastPortal, location.pathname]);

  const accountLinks = [
    {
      to: '/gestion-compte',
      icon: <FaShieldAlt className="text-emerald-600" />,
      label: t('navigation.accountManagement', { defaultValue: 'Gestion du compte' }),
      subtitle: t('navigation.accountManagementSubtitle', { defaultValue: 'Désactivation et confidentialité' }),
      active: location.pathname === '/gestion-compte',
    },
    {
      to: '/mon-compte',
      icon: <FaUserCircle className="text-blue-600" />,
      label: t('navigation.dashboard', { defaultValue: 'Tableau de bord' }),
      subtitle: t('navigation.dashboardSubtitle', { defaultValue: 'Vue d’ensemble' }),
      active: location.pathname === '/mon-compte' || location.pathname === '/dashboard-voyageur',
    },
    {
      to: '/mes-reservations',
      icon: <FaCalendarCheck className="text-red-600" />,
      label: t('navigation.myReservations', { defaultValue: 'Mes réservations' }),
      subtitle: t('navigation.myReservationsSubtitle', { defaultValue: 'Last Call et expériences' }),
      active: location.pathname === '/mes-reservations',
    },
    {
      to: '/mes-achats',
      icon: <FaShoppingBag className="text-purple-600" />,
      label: t('navigation.myPurchases', { defaultValue: 'Mes achats' }),
      subtitle: t('navigation.myPurchasesSubtitle', { defaultValue: 'Commandes et cartes' }),
      active: location.pathname === '/mes-achats',
    },
    {
      to: '/mes-favoris',
      icon: <FaHeart className="text-pink-600" />,
      label: t('navigation.myFavorites', { defaultValue: 'Mes favoris' }),
      subtitle: t('navigation.myFavoritesSubtitle', { defaultValue: 'Offres sauvegardées' }),
      active: location.pathname === '/mes-favoris',
    },
    {
      to: '/mes-alertes',
      icon: <FaBell className="text-sky-600" />,
      label: t('navigation.myAlerts', { defaultValue: 'Mes alertes' }),
      subtitle: t('navigation.myAlertsSubtitle', { defaultValue: 'Forfeo Évasion' }),
      active: location.pathname === '/mes-alertes',
    },
  ];

  useEffect(() => {
    let mounted = true;

    supabase.auth.getUser().then(({ data }) => {
      if (mounted) setCurrentUser(data?.user || null);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setCurrentUser(session?.user || null);
    });

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    if (
      !currentUser ||
      location.pathname === '/gestion-compte'
    ) {
      return () => {
        mounted = false;
      };
    }

    void supabase
      .rpc('forfeo_account_lifecycle_state')
      .maybeSingle()
      .then(({ data, error }) => {
        if (
          !mounted ||
          error ||
          !data
        ) {
          return;
        }

        const status = String(
          (data as { account_status?: unknown })
            .account_status ?? '',
        );

        if (
          status === 'deactivated' ||
          status === 'deletion_requested'
        ) {
          navigate(
            '/gestion-compte',
            { replace: true },
          );
        }
      });

    return () => {
      mounted = false;
    };
  }, [
    currentUser,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    const currentPortal = rememberForfeoPortalPath(location.pathname);
    setLastPortal(currentPortal || readRememberedForfeoPortal());
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    if (!currentUser) {
      setAvailablePortals([]);
      setLastPortal(readRememberedForfeoPortal());
      return () => {
        mounted = false;
      };
    }

    resolveForfeoPortals(supabase, currentUser)
      .then((portals) => {
        if (mounted) setAvailablePortals(portals);
      })
      .catch(() => {
        if (mounted) setAvailablePortals([]);
      });

    return () => {
      mounted = false;
    };
  }, [currentUser]);

  const handleSignOut = async () => {
    closeAllMenus();
    await supabase.auth.signOut();
    setCurrentUser(null);
    navigate('/');
  };

  const closeAllMenus = () => {
    setIsMenuOpen(false);
    setIsExploreOpen(false);
    setIsBonsPlansOpen(false);
    setIsInfoOpen(false);
    setIsAccountOpen(false);
  };

  const handleHomeClick = () => {
    closeAllMenus();

    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const handleOpenMapPage = () => {
    closeAllMenus();
    navigate('/carte');
  };

  const handleOpenSearch = () => {
    closeAllMenus();
    setIsSearchOpen(true);
  };


  const handleOpenForfyAssistant = () => {
    closeAllMenus();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('forfy:open', { detail: { mode: 'support' } }));
    }
  };

  const forfyAssistantButton = (compact = false) => (
    <button
      type="button"
      onClick={handleOpenForfyAssistant}
      aria-label="Ouvrir Forfy Assistant"
      className={`${
        compact
          ? 'h-8 w-8 sm:h-9 sm:w-9 justify-center p-0'
          : 'h-9 px-2.5 xl:h-10 xl:px-3 2xl:px-4'
      } group relative inline-flex shrink-0 items-center gap-1.5 overflow-hidden rounded-full border border-cyan-200/60 bg-slate-950/90 text-white shadow-[0_0_18px_rgba(6,182,212,0.22)] backdrop-blur-xl ring-1 ring-white/15 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300 hover:bg-slate-900 hover:shadow-[0_0_28px_rgba(59,130,246,0.34)]`}
    >
      <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/22 to-white/0 translate-x-[-135%] skew-x-[-18deg] transition-transform duration-1000 group-hover:translate-x-[135%]" />
      <span className="absolute -inset-1 rounded-full bg-cyan-400/20 blur-lg opacity-75 animate-pulse" />
      <span className="relative flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
        <FaRobot className="text-[11px] sm:text-xs text-cyan-100" />
      </span>
      {!compact && (
        <span className="relative hidden text-[9px] font-black uppercase tracking-[0.12em] xl:inline 2xl:text-[10px] 2xl:tracking-[0.18em]">
          <span className="2xl:hidden">Forfy</span>
          <span className="hidden 2xl:inline">Forfy Assistant</span>
        </span>
      )}
      <span className="relative hidden h-1.5 w-1.5 rounded-full bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.9)] sm:flex" />
    </button>
  );

  return (
    <>
      <header data-forfeo-main-header className="bg-white/95 backdrop-blur-md text-slate-900 shadow-sm sticky top-0 z-[100] font-sans border-b border-slate-200 pt-[38px] sm:pt-[40px] md:pt-[34px] lg:pt-[30px] xl:pt-0 transition-all duration-300 [&_svg]:max-h-4 [&_svg]:max-w-4 [&_svg]:shrink-0">
        <div className="container mx-auto flex items-center justify-between gap-2 px-3 pb-2 pt-1 sm:px-4 sm:pb-2.5 sm:pt-1 md:py-2.5 xl:gap-4">

          {/* Logo */}
          <div
            onClick={handleHomeClick}
            className="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-2 group cursor-pointer relative z-[101] max-w-[150px] sm:max-w-[190px] md:max-w-none"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleHomeClick();
              }
            }}
            aria-label={t('navigation.home', { defaultValue: 'Accueil' })}
          >
            <div className="flex h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9 shrink-0 items-center justify-center rounded-lg bg-slate-900 shadow-md transform group-hover:rotate-6 transition-all duration-300">
              <FaCompass className="text-white text-[13px] sm:text-sm md:text-base" />
            </div>
            <div className="flex min-w-0 items-baseline gap-1 leading-none whitespace-nowrap">
              <span className="text-[15px] sm:text-lg md:text-xl xl:text-[21px] font-black tracking-[-0.08em] text-slate-900 uppercase">FORFEO</span>
              <span className="text-[9px] sm:text-[10px] md:text-xs xl:text-sm font-medium italic tracking-[-0.05em] text-blue-600 uppercase">EXPLORE</span>
            </div>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex flex-1 items-center justify-end gap-2 2xl:gap-3">
            <button
              onClick={handleHomeClick}
              className={`shrink-0 text-[11px] font-black transition-colors uppercase tracking-[0.16em] ${
                isHome ? 'text-blue-600' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {t('navigation.home', { defaultValue: 'ACCUEIL' })}
            </button>

            {/* MENU COMPACT : INFOS / CONFIANCE / AIDE */}
            <div
              className="relative"
              onMouseEnter={() => setIsInfoOpen(true)}
              onMouseLeave={() => setIsInfoOpen(false)}
            >
              <button
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-black transition-all uppercase tracking-widest border ${
                  isInfoActive
                    ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                    : 'text-slate-600 hover:text-slate-900 border-slate-200 hover:bg-slate-50'
                }`}
                aria-haspopup="true"
                aria-expanded={isInfoOpen}
              >
                <FaBookOpen className="text-blue-500" />
                {t('navigation.infoHub', { defaultValue: 'Infos' })}
                <FaChevronDown
                  size={7}
                  className={`transition-transform ${isInfoOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isInfoOpen && (
                <div className="absolute left-0 mt-0 w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 py-3 z-[110] animate-in fade-in slide-in-from-top-2">
                  <Link
                    to="/comment-ca-marche"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isHowItWorks ? 'bg-blue-50 text-blue-800' : 'hover:bg-blue-50 text-slate-900'
                    }`}
                  >
                    <FaInfoCircle className="text-blue-500" />
                    <div>
                      <span className="block">{t('navigation.howItWorks', { defaultValue: 'Comment ça marche' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.howItWorksSubtitle', { defaultValue: 'Comprendre Forfeo' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/confiance"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isTrust ? 'bg-emerald-50 text-emerald-800' : 'hover:bg-emerald-50 text-slate-900'
                    }`}
                  >
                    <FaShieldAlt className="text-emerald-600" />
                    <div>
                      <span className="block">{t('navigation.security', { defaultValue: 'Sécurité' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.securitySubtitle', { defaultValue: 'Confiance & paiements' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/aide"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isHelp ? 'bg-sky-50 text-sky-800' : 'hover:bg-sky-50 text-slate-900'
                    }`}
                  >
                    <FaHeadset className="text-sky-500" />
                    <div>
                      <span className="block">{t('navigation.help', { defaultValue: 'Aide' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.helpSubtitle', { defaultValue: 'FAQ & support' })}</span>
                    </div>
                  </Link>

                  <div className="h-px bg-slate-100 my-2 mx-4"></div>

                  <Link
                    to="/about"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isAbout ? 'bg-slate-50 text-slate-900' : 'hover:bg-slate-50 text-slate-900'
                    }`}
                  >
                    <FaCompass className="text-slate-500" />
                    {t('navigation.about', { defaultValue: 'À propos' })}
                  </Link>

                  <Link
                    to="/service-client"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isServiceClient ? 'bg-slate-50 text-slate-900' : 'hover:bg-slate-50 text-slate-900'
                    }`}
                  >
                    <FaHeadset className="text-slate-500" />
                    {t('navigation.customerService', { defaultValue: 'Service client' })}
                  </Link>

                  <Link
                    to="/partenaires"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isPartners ? 'bg-green-50 text-green-800' : 'hover:bg-green-50 text-slate-900'
                    }`}
                  >
                    <FaStore className="text-green-600" />
                    {t('navigation.partners', { defaultValue: 'Partenaires' })}
                  </Link>

                  <Link
                    to="/offrir-un-moment"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isGiftMoment ? 'bg-pink-50 text-pink-800' : 'hover:bg-pink-50 text-slate-900'
                    }`}
                  >
                    <FaGift className="text-pink-500" />
                    {t('navigation.giftMoment', { defaultValue: 'Offrir un moment' })}
                  </Link>

                  <Link
                    to="/calendrier-des-occasions"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isOccasionCalendar ? 'bg-violet-50 text-violet-800' : 'hover:bg-violet-50 text-slate-900'
                    }`}
                  >
                    <FaCalendarCheck className="text-violet-500" />
                    <div>
                      <span className="block">{t('navigation.occasionCalendar', { defaultValue: 'Calendrier des occasions' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.occasionCalendarSubtitle', { defaultValue: 'Cadeaux toute l’année' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/revelation-surprise"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isSurpriseReveal ? 'bg-fuchsia-50 text-fuchsia-800' : 'hover:bg-fuchsia-50 text-slate-900'
                    }`}
                  >
                    <FaMagic className="text-fuchsia-500" />
                    <div>
                      <span className="block">{t('navigation.surpriseReveal', { defaultValue: 'Révélation surprise' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.surpriseRevealSubtitle', { defaultValue: 'Effet wow après achat' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/avis-experience"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isExperienceReview ? 'bg-emerald-50 text-emerald-800' : 'hover:bg-emerald-50 text-slate-900'
                    }`}
                  >
                    <FaCommentDots className="text-emerald-500" />
                    <div>
                      <span className="block">{t('navigation.experienceReview', { defaultValue: 'Avis après expérience' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.experienceReviewSubtitle', { defaultValue: 'Retour qualité Forfeo' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/carte-cadeau"
                    onClick={() => setIsInfoOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isGiftCard ? 'bg-amber-50 text-amber-800' : 'hover:bg-amber-50 text-slate-900'
                    }`}
                  >
                    <FaTicketAlt className="text-amber-500" />
                    {t('navigation.giftCard', { defaultValue: 'Carte-cadeau' })}
                  </Link>
                </div>
              )}
            </div>

            {/* MENU DÉROULANT EXPLORE */}
            <div
              className="relative"
              onMouseEnter={() => setIsExploreOpen(true)}
              onMouseLeave={() => setIsExploreOpen(false)}
            >
              <button className="flex shrink-0 items-center gap-1.5 py-2 text-[11px] font-black uppercase tracking-[0.16em] text-slate-600 transition-colors hover:text-blue-600">
                {t('navigation.explore', { defaultValue: 'Explorer' })}{' '}
                <FaChevronDown
                  size={7}
                  className={`transition-transform ${isExploreOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isExploreOpen && (
                <div className="absolute left-0 mt-0 w-72 bg-white rounded-2xl shadow-2xl border border-slate-100 py-3 z-[110] animate-in fade-in slide-in-from-top-2">

                  <Link
                    to="/habitat"
                    onClick={() => setIsExploreOpen(false)}
                    className="w-full text-left px-4 py-3 mb-2 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors border-b border-blue-100 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-lg shadow-md shadow-blue-500/30 group-hover:scale-105 group-hover:rotate-3 transition-all duration-300">
                        <FaHome className="text-white text-sm" />
                      </div>
                      <div>
                        <span className="block font-black text-blue-900 text-sm group-hover:text-blue-700 transition-colors">
                          Forfeo Habitat
                        </span>
                        <span className="block text-[10px] text-indigo-600 font-bold uppercase tracking-widest">
                          {t('navigation.habitatSubtitle', { defaultValue: 'Location au Québec' })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-blue-600 text-white text-[9px] font-black px-2 py-1 rounded-full uppercase tracking-widest shadow-md">
                        {t('common.new', { defaultValue: 'Nouveau' })}
                      </span>
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                      </span>
                    </div>
                  </Link>

                  <Link
                    to="/create-passport"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isPassport ? 'bg-indigo-50 text-indigo-800' : 'hover:bg-indigo-50 text-slate-900'
                    }`}
                  >
                    <FaPassport className={isPassport ? 'text-indigo-600' : 'text-indigo-500'} />{' '}
                    {t('navigation.passport', { defaultValue: 'Mon Passeport Forfeo' })}
                  </Link>

                  <Link
                    to="/explorer-plus"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isExplorerPlus ? 'bg-blue-50 text-blue-800' : 'hover:bg-blue-50 text-slate-900'
                    }`}
                  >
                    <FaHotel className="text-blue-500" />{' '}
                    {t('navigation.accommodations', { defaultValue: 'Nos Hébergements' })}
                  </Link>

                  <button
                    onClick={handleOpenMapPage}
                    className="w-full text-left px-5 py-3 hover:bg-emerald-50 flex items-center gap-3 text-slate-900 font-bold text-sm transition"
                  >
                    <FaMapMarkedAlt className="text-emerald-600" />{' '}
                    {t('navigation.interactiveMap', { defaultValue: 'Carte interactive' })}
                  </button>

                  <Link
                    to="/visiter-quebec"
                    data-navigation-item="traveler-toolbox"
                    onClick={() => setIsExploreOpen(false)}
                    className={`mx-3 my-2 flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${
                      isVisitQuebec
                        ? 'border-sky-300 bg-gradient-to-r from-sky-100 to-cyan-50 shadow-md shadow-sky-100'
                        : 'border-sky-100 bg-gradient-to-r from-sky-50 to-cyan-50 hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100'
                    }`}
                  >
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                      isVisitQuebec
                        ? 'bg-sky-600 text-white'
                        : 'bg-white text-sky-600 shadow-sm'
                    }`}>
                      <FaMapMarkerAlt size={17} />
                    </div>

                    <div className="min-w-0">
                      <span className={`block text-sm font-black leading-tight ${
                        isVisitQuebec ? 'text-sky-950' : 'text-slate-900'
                      }`}>
                        {i18n.resolvedLanguage?.toLowerCase().startsWith('en')
                          ? 'Traveler toolbox'
                          : 'Boîte à outils du voyageur'}
                      </span>

                      <span className="mt-1 block text-[10px] font-bold uppercase leading-snug tracking-[0.08em] text-sky-700">
                        {i18n.resolvedLanguage?.toLowerCase().startsWith('en')
                          ? 'Weather, events, health and live information'
                          : 'Météo, événements, santé et infos en direct'}
                      </span>
                    </div>
                  </Link>

                  <Link
                    to="/decouvrir-quebec"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isDiscoverQuebec ? 'bg-emerald-50 text-emerald-800' : 'hover:bg-emerald-50 text-slate-900'
                    }`}
                  >
                    <FaMapMarkedAlt className={isDiscoverQuebec ? 'text-emerald-600' : 'text-emerald-500'} />{' '}
                    <div>
                      <span className="block">{t('navigation.discoverQuebec', { defaultValue: 'Découvrir le Québec' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.discoverQuebecSubtitle', { defaultValue: 'Guide voyage FR/EN' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/offrir-un-moment"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isGiftMoment ? 'bg-pink-50 text-pink-800' : 'hover:bg-pink-50 text-slate-900'
                    }`}
                  >
                    <FaGift className={isGiftMoment ? 'text-pink-600' : 'text-pink-500'} />{' '}
                    <div>
                      <span className="block">{t('navigation.giftMoment', { defaultValue: 'Offrir un moment' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.giftMomentSubtitle', { defaultValue: 'Passerelle vers forfeo.com' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/revelation-surprise"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isSurpriseReveal ? 'bg-fuchsia-50 text-fuchsia-800' : 'hover:bg-fuchsia-50 text-slate-900'
                    }`}
                  >
                    <FaMagic className={isSurpriseReveal ? 'text-fuchsia-600' : 'text-fuchsia-500'} />{' '}
                    <div>
                      <span className="block">{t('navigation.surpriseReveal', { defaultValue: 'Révélation surprise' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.surpriseRevealSubtitle', { defaultValue: 'Après achat / cadeau' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/avis-experience"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isExperienceReview ? 'bg-emerald-50 text-emerald-800' : 'hover:bg-emerald-50 text-slate-900'
                    }`}
                  >
                    <FaCommentDots className={isExperienceReview ? 'text-emerald-600' : 'text-emerald-500'} />{' '}
                    <div>
                      <span className="block">{t('navigation.experienceReview', { defaultValue: 'Avis après expérience' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.experienceReviewSubtitle', { defaultValue: 'Après visite / voucher' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/calendrier-des-occasions"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isOccasionCalendar ? 'bg-violet-50 text-violet-800' : 'hover:bg-violet-50 text-slate-900'
                    }`}
                  >
                    <FaCalendarCheck className={isOccasionCalendar ? 'text-violet-600' : 'text-violet-500'} />{' '}
                    <div>
                      <span className="block">{t('navigation.occasionCalendar', { defaultValue: 'Calendrier des occasions' })}</span>
                      <span className="block text-[10px] text-slate-400 uppercase tracking-widest">{t('navigation.occasionCalendarSubtitle', { defaultValue: 'Idées selon les dates' })}</span>
                    </div>
                  </Link>

                  <Link
                    to="/reseau-local"
                    onClick={() => setIsExploreOpen(false)}
                    className="w-full text-left px-5 py-3 hover:bg-slate-50 flex items-center gap-3 text-slate-900 font-bold text-sm transition"
                  >
                    <FaUsers className="text-blue-400" />{' '}
                    {t('navigation.localNetwork', { defaultValue: 'Réseau Local' })}
                  </Link>

                  <Link
                    to="/carte-cadeau"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isGiftCard ? 'bg-amber-50 text-amber-800' : 'hover:bg-amber-50 text-slate-900'
                    }`}
                  >
                    <FaTicketAlt className="text-amber-500" />{' '}
                    {t('navigation.giftCard', { defaultValue: 'Carte-cadeau' })}
                  </Link>

                  <Link
                    to="/aide"
                    onClick={() => setIsExploreOpen(false)}
                    className={`w-full text-left px-5 py-3 flex items-center gap-3 font-bold text-sm transition ${
                      isHelp ? 'bg-blue-50 text-blue-800' : 'hover:bg-blue-50 text-slate-900'
                    }`}
                  >
                    <FaHeadset className="text-blue-500" />{' '}
                    {t('navigation.help', { defaultValue: 'Aide' })}
                  </Link>

                  <div className="h-px bg-slate-100 my-2 mx-4"></div>

                  <Link
                    to="/login?type=business&mode=login"
                    onClick={() => setIsExploreOpen(false)}
                    className="w-full text-left px-5 py-3 hover:bg-green-50 flex items-center gap-3 text-green-700 font-bold text-sm transition"
                  >
                    <FaPlusCircle />{' '}
                    {t('navigation.registerBusiness', { defaultValue: 'Inscrire mon Entreprise' })}
                  </Link>
                </div>
              )}
            </div>

            {/* MENU DÉROULANT BONS PLANS */}
            <div
              className="relative"
              onMouseEnter={() => setIsBonsPlansOpen(true)}
              onMouseLeave={() => setIsBonsPlansOpen(false)}
            >
              <button className="flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 px-3 text-[9px] font-black uppercase tracking-[0.16em] text-white shadow-lg transition-all hover:-translate-y-0.5 hover:from-purple-500 hover:via-pink-400 hover:to-orange-400 hover:shadow-pink-500/40 2xl:px-4 2xl:text-[10px]">
                <FaFire className="text-yellow-200 text-xs" />
                {t('navigation.deals', { defaultValue: 'Bons Plans' })}
                <FaChevronDown
                  size={7}
                  className={`transition-transform opacity-80 ${isBonsPlansOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isBonsPlansOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 pt-4 w-72 z-[110] animate-in fade-in slide-in-from-top-2">
                  <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden relative">
                    <div className="h-1.5 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500"></div>

                    <div className="py-2">
                      <Link
                        to="/drops"
                        onClick={() => setIsBonsPlansOpen(false)}
                        className={`w-full text-left px-5 py-4 flex items-center gap-4 transition ${isDrops ? 'bg-cyan-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className={`p-2.5 rounded-xl ${isDrops ? 'bg-cyan-200' : 'bg-cyan-100'}`}>
                          <FaLocationArrow className="text-cyan-600 text-lg animate-pulse" />
                        </div>
                        <div>
                          <p className={`font-black text-sm ${isDrops ? 'text-cyan-900' : 'text-slate-900'}`}>
                            Forfeo Drops
                          </p>
                          <p className="text-xs text-slate-500 font-medium">
                            {t('navigation.dropsSubtitle', { defaultValue: 'Récompenses géolocalisées' })}
                          </p>
                        </div>
                      </Link>

                      <Link
                        to="/prevente"
                        onClick={() => setIsBonsPlansOpen(false)}
                        className={`w-full text-left px-5 py-4 flex items-center gap-4 transition ${isPrevente ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className={`p-2.5 rounded-xl ${isPrevente ? 'bg-blue-200' : 'bg-blue-100'}`}>
                          <FaTicketAlt className="text-blue-600 text-lg" />
                        </div>
                        <div>
                          <p className={`font-black text-sm ${isPrevente ? 'text-blue-900' : 'text-slate-900'}`}>
                            {t('navigation.vipPresale', { defaultValue: 'Prévente VIP' })}
                          </p>
                          <p className="text-xs text-slate-500 font-medium">
                            {t('navigation.vipPresaleSubtitle', { defaultValue: 'Forfaits limités & accès sécurisé' })}
                          </p>
                        </div>
                      </Link>

                      <Link
                        to="/boutique"
                        onClick={() => setIsBonsPlansOpen(false)}
                        className={`w-full text-left px-5 py-4 flex items-center gap-4 transition ${isBoutique ? 'bg-purple-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className={`p-2.5 rounded-xl ${isBoutique ? 'bg-purple-200' : 'bg-purple-100'}`}>
                          <FaStore className="text-purple-600 text-sm" />
                        </div>
                        <div>
                          <p className={`font-black text-sm ${isBoutique ? 'text-purple-900' : 'text-slate-900'}`}>
                            {t('navigation.shop', { defaultValue: 'Boutique Forfeo' })}
                          </p>
                          <p className="text-xs text-slate-500 font-medium">
                            {t('navigation.shopSubtitle', { defaultValue: 'Forfaits et expériences' })}
                          </p>
                        </div>
                      </Link>

                      <Link
                        to="/forfeo-finds"
                        onClick={() => setIsBonsPlansOpen(false)}
                        className={`w-full text-left px-5 py-4 flex items-center gap-4 transition ${isForfeoFinds ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className={`p-2.5 rounded-xl ${isForfeoFinds ? 'bg-orange-200' : 'bg-orange-100'}`}>
                          <FaFire className="text-orange-600 text-lg" />
                        </div>
                        <div>
                          <p className={`font-black text-sm ${isForfeoFinds ? 'text-orange-900' : 'text-slate-900'}`}>
                            Forfeo Finds
                          </p>
                          <p className="text-xs text-slate-500 font-medium">
                            {t('navigation.findsSubtitle', { defaultValue: 'Découvertes exclusives' })}
                          </p>
                        </div>
                      </Link>

                      {/* FORFEO ÉVASION */}
                      <Link
                        to="/alertes-intelligentes"
                        onClick={() => setIsBonsPlansOpen(false)}
                        className={`w-full text-left px-5 py-4 flex items-center gap-4 transition ${isEvasion ? 'bg-sky-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className={`p-2.5 rounded-xl ${isEvasion ? 'bg-sky-200' : 'bg-sky-100'}`}>
                          <FaPlane className="text-sky-600 text-lg" />
                        </div>
                        <div>
                          <p className={`font-black text-sm ${isEvasion ? 'text-sky-900' : 'text-slate-900'}`}>
                            {t('navigation.evasion', { defaultValue: 'Évasion' })}
                          </p>
                          <p className="text-xs text-slate-500 font-medium">
                            {t('navigation.evasionSubtitle', { defaultValue: 'Alertes voyage intelligentes' })}
                          </p>
                        </div>
                      </Link>

                      <Link
                        to="/last-call"
                        onClick={() => setIsBonsPlansOpen(false)}
                        className={`w-full text-left px-5 py-4 flex items-center gap-4 transition ${isLastCall ? 'bg-red-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className={`p-2 rounded-lg ${isLastCall ? 'bg-red-200' : 'bg-red-100'}`}>
                          <FaBolt className="text-red-600 text-sm" />
                        </div>
                        <div>
                          <p className={`font-black text-sm ${isLastCall ? 'text-red-900' : 'text-slate-900'}`}>
                            {t('navigation.flashDeals', { defaultValue: 'Offres Flash' })}
                          </p>
                          <p className="text-xs text-slate-500 font-medium">
                            {t('navigation.lastCallSubtitle', { defaultValue: 'Dernière minute (Last Call)' })}
                          </p>
                        </div>
                      </Link>

                      <Link
                        to="/club"
                        onClick={() => setIsBonsPlansOpen(false)}
                        className={`w-full text-left px-5 py-4 flex items-center gap-4 transition ${isClub ? 'bg-yellow-50' : 'hover:bg-slate-50'}`}
                      >
                        <div className={`p-2 rounded-lg ${isClub ? 'bg-yellow-200' : 'bg-yellow-100'}`}>
                          <FaCrown className="text-yellow-600 text-sm" />
                        </div>
                        <div>
                          <p className={`font-black text-sm ${isClub ? 'text-yellow-900' : 'text-slate-900'}`}>
                            Club Forfeo
                          </p>
                          <p className="text-xs text-slate-500 font-medium">
                            {t('navigation.clubSubtitle', { defaultValue: 'Avantages et récompenses' })}
                          </p>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Recherche Globale Desktop */}
            <button
              onClick={handleOpenSearch}
              className="group ml-1 flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-slate-100 bg-slate-50 px-2.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-900 2xl:px-3"
            >
              <FaSearch size={13} className="group-hover:scale-110 transition-transform text-blue-600" />
              <span className="hidden text-[9px] font-black uppercase tracking-widest text-slate-500 2xl:inline">
                {t('navigation.search', { defaultValue: 'Recherche' })}
              </span>
            </button>

            {forfyAssistantButton(false)}

            {/* Panier */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-50 hover:text-slate-900 group"
              aria-label={t('cart.open', { defaultValue: 'Ouvrir le panier' })}
            >
              <FaShoppingCart size={15} className="group-hover:scale-110 transition-transform" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] font-black w-4 h-4 flex items-center justify-center rounded-full animate-bounce shadow-sm border border-white">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Forfeo Reserve (icône compacte) */}
            <Link
              to="/reserve"
              aria-label="Forfeo Reserve"
              title="Forfeo Reserve"
              className="relative ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-50 hover:text-emerald-600 group"
            >
              <FaCalendarCheck size={15} className="group-hover:scale-110 transition-transform" />
            </Link>

            <div className="h-6 w-px bg-slate-200 mx-1"></div>

            {/* MENU COMPTE INTELLIGENT / CONNEXION */}
            {isLoggedIn ? (
              <div
                className="relative"
                onMouseEnter={() => setIsAccountOpen(true)}
                onMouseLeave={() => setIsAccountOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setIsAccountOpen((v) => !v)}
                  className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-[11px] font-black uppercase tracking-[0.16em] transition-all ${
                    isAccountActive
                      ? 'bg-slate-900 text-white shadow-lg'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  aria-label={t('navigation.myAccount', { defaultValue: 'Mon compte' })}
                >
                  <FaUserCircle className="text-[13px]" />
                  <span className="max-w-[82px] truncate 2xl:max-w-[110px]">{accountLabel}</span>
                  <FaChevronDown
                    size={7}
                    className={`transition-transform ${isAccountOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isAccountOpen && (
                  <div className="absolute right-0 mt-0 w-80 bg-white rounded-3xl shadow-2xl border border-slate-100 py-3 z-[110] animate-in fade-in slide-in-from-top-2 overflow-hidden">
                    <div className="px-5 py-4 bg-gradient-to-r from-slate-900 to-blue-900 text-white">
                      <div className="text-[10px] uppercase tracking-widest font-black text-blue-200">
                        {t('navigation.connectedSpace', { defaultValue: 'Espace connecté' })}
                      </div>
                      <div className="mt-1 font-black text-sm truncate">{accountLabel}</div>
                      <div className="text-xs text-slate-300 truncate">{currentUser?.email}</div>
                    </div>

                    <div className="py-2">
                      {smartReturnPortal && (
                        <Link
                          to={smartReturnPortal.to}
                          onClick={() => setIsAccountOpen(false)}
                          className="mx-3 mb-2 px-4 py-3 rounded-2xl bg-slate-900 text-white flex items-center gap-3 hover:bg-blue-900 transition shadow-lg"
                        >
                          <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
                            {portalIcon(smartReturnPortal)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-black text-sm flex items-center gap-2">
                              Retour à mon portail <FaArrowRight size={10} />
                            </div>
                            <div className="text-xs text-white/60 font-medium truncate">{smartReturnPortal.label}</div>
                          </div>
                        </Link>
                      )}

                      {portalLinks.length > 0 && (
                        <div className="px-5 pt-2 pb-1 text-[10px] uppercase tracking-widest font-black text-slate-400">
                          Mes portails Forfeo
                        </div>
                      )}

                      {portalLinks.map((item) => (
                        <Link
                          key={item.id}
                          to={item.to}
                          onClick={() => setIsAccountOpen(false)}
                          className={`w-full px-5 py-3 flex items-center gap-3 transition ${
                            item.active ? 'bg-indigo-50 text-indigo-900' : 'hover:bg-slate-50 text-slate-900'
                          }`}
                        >
                          <div className="w-10 h-10 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                            {item.icon}
                          </div>
                          <div className="min-w-0">
                            <div className="font-black text-sm">{item.label}</div>
                            <div className="text-xs text-slate-500 font-medium truncate">{item.subtitle}</div>
                          </div>
                        </Link>
                      ))}

                      {portalLinks.length > 0 && <div className="h-px bg-slate-100 my-2 mx-5" />}

                      {accountLinks.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setIsAccountOpen(false)}
                          className={`w-full px-5 py-3 flex items-center gap-3 transition ${
                            item.active ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50 text-slate-900'
                          }`}
                        >
                          <div className="w-10 h-10 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                            {item.icon}
                          </div>
                          <div>
                            <div className="font-black text-sm">{item.label}</div>
                            <div className="text-xs text-slate-500 font-medium">{item.subtitle}</div>
                          </div>
                        </Link>
                      ))}

                      <div className="h-px bg-slate-100 my-2 mx-5" />

                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="w-full px-5 py-3 flex items-center gap-3 text-left text-red-600 hover:bg-red-50 transition font-black text-sm"
                      >
                        <div className="w-10 h-10 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                          <FaSignOutAlt />
                        </div>
                        {t('navigation.logout', { defaultValue: 'Déconnexion' })}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="px-1.5 text-[11px] font-bold text-slate-500 transition-colors hover:text-slate-900 2xl:px-2 2xl:text-xs"
              >
                {t('navigation.login', { defaultValue: 'Connexion' })}
              </Link>
            )}

            {/* SÉLECTEUR DE LANGUE GLOBAL (Desktop) */}
            <LanguageSwitcher compact className="ml-1" />
          </nav>

          {/* Mobile Toggle Icons */}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2 xl:hidden relative z-[101]">

            {/* Recherche Mobile */}
            <button
              onClick={handleOpenSearch}
              className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600 shadow-sm transition hover:bg-blue-100"
              aria-label={t('navigation.search', { defaultValue: 'Recherche' })}
            >
              <FaSearch size={15} />
            </button>

            {forfyAssistantButton(true)}

            <button
              onClick={() => setIsCartOpen(true)}
              className="relative flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-slate-50 text-slate-900"
              aria-label={t('cart.open', { defaultValue: 'Ouvrir le panier' })}
            >
              <FaShoppingCart size={15} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] font-black w-4 h-4 flex items-center justify-center rounded-full border border-white">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Forfeo Reserve (icône compacte) */}
            <Link
              to="/reserve"
              aria-label="Forfeo Reserve"
              title="Forfeo Reserve"
              className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 shadow-sm transition hover:bg-emerald-100"
            >
              <FaCalendarCheck size={15} />
            </Link>

            <button
              className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-slate-50 text-slate-900 shadow-sm transition hover:bg-slate-100"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={
                isMenuOpen
                  ? t('navigation.closeMenu', { defaultValue: 'Fermer le menu' })
                  : t('navigation.openMenu', { defaultValue: 'Ouvrir le menu' })
              }
            >
              {isMenuOpen ? <FaTimes size={15} /> : <FaBars size={15} />}
            </button>
          </div>
        </div>
      </header>

      {/* Menu Mobile */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="xl:hidden fixed inset-x-0 bottom-0 top-[var(--header-h,0px)] z-[99] bg-white w-full overflow-y-auto shadow-2xl [&_svg]:max-h-4 [&_svg]:max-w-4 [&_svg]:shrink-0"
          >
            <div className="p-6 flex flex-col space-y-6 pb-24">

              {/* SÉLECTEUR DE LANGUE (MOBILE) */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <label className="block text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
                  {t('language.label', { defaultValue: 'Langue' })}
                </label>
                <LanguageSwitcher compact className="w-full justify-center" />
              </div>

              {/* Navigation Rapide existante + NOUVEAU */}
              <div className="bg-slate-50 p-4 rounded-3xl border border-slate-100">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3">
                  {t('navigation.quickNavigation', { defaultValue: 'Navigation Rapide' })}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Link
                    to="/drops"
                    className={`p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm transition border ${
                      isDrops ? 'text-cyan-700 border-cyan-200 bg-cyan-50' : 'bg-white text-cyan-600 border-cyan-100'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaLocationArrow size={22} className={isDrops ? '' : 'animate-pulse'} />
                    <span className="font-black text-[10px] uppercase tracking-widest">Drops</span>
                  </Link>

                  <Link
                    to="/alertes-intelligentes"
                    className={`p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm transition border ${
                      isEvasion ? 'text-sky-700 border-sky-200 bg-sky-50' : 'bg-white text-sky-600 border-sky-100'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaPlane size={22} />
                    <span className="font-black text-[10px] uppercase tracking-widest">
                      {t('navigation.evasion', { defaultValue: 'Évasion' })}
                    </span>
                  </Link>

                  <Link
                    to="/last-call"
                    className={`p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm transition border ${
                      isLastCall ? 'text-red-700 border-red-200 bg-red-50' : 'bg-white text-red-600 border-red-100'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaBolt size={22} />
                    <span className="font-black text-[10px] uppercase tracking-widest">
                      {t('navigation.flash', { defaultValue: 'Flash' })}
                    </span>
                  </Link>

                  <Link
                    to="/forfeo-finds"
                    className={`p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm transition border ${
                      isForfeoFinds ? 'text-orange-700 border-orange-200 bg-orange-50' : 'bg-white text-orange-600 border-orange-100'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaFire size={22} />
                    <span className="font-black text-[10px] uppercase tracking-widest">Finds</span>
                  </Link>

                  <Link
                    to="/explorer-plus"
                    className={`p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm transition border ${
                      isExplorerPlus ? 'text-blue-700 border-blue-200 bg-blue-50' : 'bg-white text-blue-600 border-blue-100'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaHotel size={22} />
                    <span className="font-black text-[10px] uppercase tracking-widest">
                      {t('navigation.hotels', { defaultValue: 'Hôtels' })}
                    </span>
                  </Link>

                  <Link
                    to="/visiter-quebec"
                    className={`p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm transition border ${
                      isVisitQuebec ? 'text-sky-700 border-sky-200 bg-sky-50' : 'bg-white text-sky-600 border-sky-100'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaMapMarkerAlt size={22} />
                    <span className="font-black text-[10px] uppercase tracking-widest text-center leading-tight">
                      {t('navigation.toolboxShort', { defaultValue: 'La Boîte à\nOutils' })}
                    </span>
                  </Link>


                  <Link
                    to="/decouvrir-quebec"
                    className={`p-4 rounded-2xl flex flex-col items-center justify-center gap-2 shadow-sm transition border ${
                      isDiscoverQuebec ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : 'bg-white text-emerald-600 border-emerald-100'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaMapMarkedAlt size={22} />
                    <span className="font-black text-[10px] uppercase tracking-widest text-center leading-tight">
                      {t('navigation.discoverQuebecShort', { defaultValue: 'Découvrir\nQuébec' })}
                    </span>
                  </Link>
                </div>
              </div>

              {/* Liste des liens */}
              <div className="space-y-2">

                <Link
                  to="/habitat"
                  className={`w-full text-left py-4 px-4 mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border rounded-2xl flex justify-between items-center group shadow-sm ${
                    isHabitat ? 'border-blue-300' : 'border-blue-100'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2.5 rounded-xl shadow-md shadow-blue-500/30">
                      <FaHome className="text-white text-lg animate-pulse" />
                    </div>
                    <div>
                      <span className="block font-black text-blue-900 text-base">
                        Forfeo Habitat
                      </span>
                      <span className="block text-[10px] text-indigo-600 font-bold uppercase tracking-widest">
                        {t('navigation.habitatMobileSubtitle', { defaultValue: 'Location Résidentielle' })}
                      </span>
                    </div>
                  </div>
                  <span className="bg-blue-600 text-white text-[10px] px-2 py-1 rounded-full uppercase tracking-widest shadow-sm">
                    {t('common.new', { defaultValue: 'Nouveau' })}
                  </span>
                </Link>

                <Link
                  to="/create-passport"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isPassport ? 'text-indigo-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="flex items-center gap-2">
                    <FaPassport className="text-indigo-500" />{' '}
                    {t('navigation.passport', { defaultValue: 'Mon Passeport Forfeo' })}
                  </span>
                  <span className="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-1 rounded-full uppercase tracking-widest">
                    {t('common.new', { defaultValue: 'Nouveau' })}
                  </span>
                </Link>

                <Link
                  to="/drops"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isDrops ? 'text-cyan-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="flex items-center gap-2">
                    <FaLocationArrow className="text-cyan-500 animate-pulse" /> Forfeo Drops
                  </span>
                  <span className="bg-cyan-100 text-cyan-700 text-[10px] px-2 py-1 rounded-full uppercase tracking-widest">
                    {t('common.local', { defaultValue: 'Local' })}
                  </span>
                </Link>

                <Link
                  to="/prevente"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isPrevente ? 'text-blue-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.vipPresale', { defaultValue: 'Prévente VIP' })}{' '}
                  <FaTicketAlt className={isPrevente ? 'text-blue-400' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/boutique"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isBoutique ? 'text-purple-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="flex items-center gap-2">
                    <FaFire className="text-pink-500" />{' '}
                    {t('navigation.dealsShop', { defaultValue: 'Bons Plans (Boutique)' })}
                  </span>
                  <FaStore className={isBoutique ? 'text-purple-400' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/forfeo-finds"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isForfeoFinds ? 'text-orange-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Forfeo Finds{' '}
                  <FaFire className={isForfeoFinds ? 'text-orange-400' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/alertes-intelligentes"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isEvasion ? 'text-sky-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Forfeo {t('navigation.evasion', { defaultValue: 'Évasion' })}{' '}
                  <FaPlane className={isEvasion ? 'text-sky-400' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/club"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isClub ? 'text-yellow-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="flex items-center gap-2">
                    <FaCrown className="text-yellow-500" /> Club Forfeo
                  </span>
                </Link>

                <Link
                  to="/visiter-quebec"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isVisitQuebec ? 'text-sky-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.toolbox', { defaultValue: 'La Boîte à Outils' })}{' '}
                  <FaMapMarkerAlt className={isVisitQuebec ? 'text-sky-400' : 'text-slate-300'} />
                </Link>


                <Link
                  to="/decouvrir-quebec"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isDiscoverQuebec ? 'text-emerald-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.discoverQuebec', { defaultValue: 'Découvrir le Québec' })}{' '}
                  <FaMapMarkedAlt className={isDiscoverQuebec ? 'text-emerald-400' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/calendrier-des-occasions"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isOccasionCalendar ? 'text-violet-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.occasionCalendar', { defaultValue: 'Calendrier des occasions' })}{' '}
                  <FaCalendarCheck className={isOccasionCalendar ? 'text-violet-400' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/revelation-surprise"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isSurpriseReveal ? 'text-fuchsia-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.surpriseReveal', { defaultValue: 'Révélation surprise' })}{' '}
                  <FaMagic className={isSurpriseReveal ? 'text-fuchsia-400' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/avis-experience"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isExperienceReview ? 'text-emerald-700' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.experienceReview', { defaultValue: 'Avis après expérience' })}{' '}
                  <FaCommentDots className={isExperienceReview ? 'text-emerald-400' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/service-client"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isServiceClient ? 'text-blue-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.customerService', { defaultValue: 'Service Client' })}{' '}
                  <FaHeadset className={isServiceClient ? 'text-blue-500' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/about"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isAbout ? 'text-blue-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.about', { defaultValue: 'À Propos' })}{' '}
                  <FaInfoCircle className={isAbout ? 'text-blue-400' : 'text-slate-300'} />
                </Link>

                <button
                  className="w-full text-left text-lg font-bold text-slate-900 py-3 border-b border-slate-100 flex justify-between items-center"
                  onClick={handleOpenMapPage}
                >
                  {t('navigation.regions', { defaultValue: 'Nos Régions' })}{' '}
                  <FaMapMarkedAlt className="text-slate-300" />
                </button>

                <button
                  className="w-full text-left text-lg font-bold text-slate-900 py-3 border-b border-slate-100 flex justify-between items-center"
                  onClick={handleOpenMapPage}
                >
                  {t('navigation.interactiveMap', { defaultValue: 'Carte Interactive' })}{' '}
                  <FaCompass className="text-slate-300" />
                </button>

                <Link
                  to="/reseau-local"
                  className="w-full text-left text-lg font-bold text-slate-900 py-3 border-b border-slate-100 flex justify-between items-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.localNetwork', { defaultValue: 'Réseau Local' })}{' '}
                  <FaUsers className="text-slate-300" />
                </Link>

                <Link
                  to="/comment-ca-marche"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isHowItWorks ? 'text-blue-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.howItWorks', { defaultValue: 'Comment ça marche' })}{' '}
                  <FaInfoCircle className={isHowItWorks ? 'text-blue-500' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/confiance"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isTrust ? 'text-blue-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.trust', { defaultValue: 'Confiance' })}{' '}
                  <FaShieldAlt className={isTrust ? 'text-blue-500' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/aide"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isHelp ? 'text-blue-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.help', { defaultValue: 'Aide' })}{' '}
                  <FaHeadset className={isHelp ? 'text-blue-500' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/offrir-un-moment"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isGiftMoment ? 'text-pink-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.giftMoment', { defaultValue: 'Offrir un moment' })}{' '}
                  <FaGift className={isGiftMoment ? 'text-pink-500' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/carte-cadeau"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isGiftCard ? 'text-amber-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.giftCard', { defaultValue: 'Carte-cadeau' })}{' '}
                  <FaTicketAlt className={isGiftCard ? 'text-amber-500' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/partenaires"
                  className={`w-full text-left text-lg font-bold py-3 border-b border-slate-100 flex justify-between items-center ${
                    isPartners ? 'text-green-600' : 'text-slate-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.partners', { defaultValue: 'Partenaires' })}{' '}
                  <FaStore className={isPartners ? 'text-green-500' : 'text-slate-300'} />
                </Link>

                <Link
                  to="/login?type=business&mode=login"
                  className="w-full text-left text-lg font-bold text-green-600 py-3 border-b border-slate-100 flex justify-between items-center"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('navigation.businessSpace', { defaultValue: 'Espace Entreprise' })}{' '}
                  <FaPlusCircle className="text-green-200" />
                </Link>
              </div>

              {/* MENU COMPTE / CONNEXION MOBILE */}
              <div className="pt-2 space-y-3">
                {isLoggedIn ? (
                  <div className="rounded-3xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
                        <FaUserCircle size={24} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-black text-slate-400">
                          {t('navigation.connectedSpace', { defaultValue: 'Espace connecté' })}
                        </div>
                        <div className="font-black text-slate-900 truncate">{accountLabel}</div>
                        <div className="text-xs text-slate-500 truncate">{currentUser?.email}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-2">
                      {smartReturnPortal && (
                        <Link
                          to={smartReturnPortal.to}
                          className="flex items-center justify-between rounded-2xl bg-slate-900 px-4 py-4 font-black text-white shadow-lg"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <span className="flex items-center gap-3">
                            {portalIcon(smartReturnPortal)}
                            Retour à mon portail
                          </span>
                          <FaArrowRight size={12} className="text-white/50" />
                        </Link>
                      )}

                      {portalLinks.length > 0 && (
                        <div className="px-1 pt-2 text-[10px] uppercase tracking-widest font-black text-slate-400">
                          Mes portails Forfeo
                        </div>
                      )}

                      {portalLinks.map((item) => (
                        <Link
                          key={item.id}
                          to={item.to}
                          className={`flex items-center justify-between rounded-2xl bg-white px-4 py-3 font-bold transition ${
                            item.active ? 'text-indigo-700 ring-2 ring-indigo-100' : 'text-slate-800'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <span className="flex items-center gap-3">
                            {item.icon}
                            {item.label}
                          </span>
                          <FaArrowRight size={12} className="text-slate-300" />
                        </Link>
                      ))}

                      {accountLinks.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={`flex items-center justify-between rounded-2xl bg-white px-4 py-3 font-bold transition ${
                            item.active ? 'text-blue-700 ring-2 ring-blue-100' : 'text-slate-800'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <span className="flex items-center gap-3">
                            {item.icon}
                            {item.label}
                          </span>
                          <FaArrowRight size={12} className="text-slate-300" />
                        </Link>
                      ))}

                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="flex items-center justify-center gap-3 bg-red-50 text-red-600 py-4 rounded-2xl font-black uppercase tracking-widest text-sm"
                      >
                        <FaSignOutAlt /> {t('navigation.logout', { defaultValue: 'Déconnexion' })}
                      </button>
                    </div>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center justify-center gap-3 bg-slate-900 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaUserCircle /> {t('navigation.login', { defaultValue: 'Connexion' })}
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modale de Recherche Globale */}
      <SearchOmnibar isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Header;
